/**
 * Logo Reveal Handler Module with Progressive Scroll Animation
 * Shows/hides logo based on scroll position (homepage only) and adds position-based animation (all pages)
 */

const handleLogoReveal = () => {
	// DOM elements
	const homepageLogo = document.querySelector('.js-logo');
	const header = document.querySelector('.header');
	const universalLogo = document.querySelector('.logo-fixed') || homepageLogo;

	// Early return if no logo found
	if (!universalLogo) {
		console.warn('Logo element not found');
		return;
	}

	// Cache logo parts for better performance
	const logoRightSide = universalLogo.querySelector('.ico-logo-right-side');

	// Configuration constants
	const CONFIG = {
		MAX_TRANSFORM: 50, // Maximum upward movement in pixels
		MOVEMENT_RATIO: 1, // 1:1 ratio with scroll movement (adjust if needed)
		LOGO_FADE_DURATION: 500, // Shorter delay - 0.5s after fade starts
		ANIMATION_ENABLED: false // Track if right-hand side animation is enabled
	};

	// State variables
	let ticking = false;
	let lastScrollY = window.scrollY;
	let currentTransform = 0; // Track current transform value
	let logoVisible = false; // Track logo visibility state
	let fadeTimeout = null; // Track fade timeout

	// Check if this is homepage with special logo behavior
	const isHomepage = () => {
		return homepageLogo && header;
	};

	// Get logo and header positions (for homepage reveal only)
	const getPositions = () => {
		if (!homepageLogo || !header) return { logoOffsetTop: 0, headerOffset: 0 };

		const logoRect = homepageLogo.getBoundingClientRect();
		const headerRect = header.getBoundingClientRect();
		const logoOffsetTop = logoRect.top + window.scrollY;
		const headerOffset = headerRect.top + window.scrollY + headerRect.height;

		return { logoOffsetTop, headerOffset };
	};

	// Reset logo transforms to initial state
	const resetLogoTransforms = () => {
		if (logoRightSide) logoRightSide.style.transform = 'translateY(0px)';
		universalLogo.classList.remove('is-scrolling');
		currentTransform = 0;
		CONFIG.ANIMATION_ENABLED = false;
	};

	// Handle logo fade-in with right-hand side animation timing
	const fadeInLogo = () => {
		if (!homepageLogo) return;

		// Start fade-in
		homepageLogo.classList.add('is-visible');
		logoVisible = true;

		// Clear any existing timeout
		if (fadeTimeout) clearTimeout(fadeTimeout);

		// Enable right-hand side animation after shorter delay
		fadeTimeout = setTimeout(() => {
			CONFIG.ANIMATION_ENABLED = true;
			// Reset right-hand side to bottom position to start animation
			currentTransform = 0;
			if (logoRightSide) logoRightSide.style.transform = 'translateY(0px)';
		}, CONFIG.LOGO_FADE_DURATION);
	};

	// Handle logo fade-out with right-hand side animation timing
	const fadeOutLogo = () => {
		if (!homepageLogo) return;

		// Clear any existing timeout
		if (fadeTimeout) clearTimeout(fadeTimeout);

		// First restore right-hand side to bottom position
		CONFIG.ANIMATION_ENABLED = false;
		currentTransform = 0;
		if (logoRightSide) {
			logoRightSide.style.transform = 'translateY(0px)';
			universalLogo.classList.remove('is-scrolling');
		}

		// Then start fade-out after a brief delay to ensure restoration is visible
		fadeTimeout = setTimeout(() => {
			homepageLogo.classList.remove('is-visible');
			logoVisible = false;
		}, 100); // Small delay to ensure right-hand side restoration is seen
	};

	// Apply direction-based scroll animation to logo parts
	const applyDirectionalAnimation = () => {
		if (!logoRightSide) return;

		const currentScrollY = window.scrollY;
		const scrollDelta = currentScrollY - lastScrollY;

		// Special case: Reset to bottom when at the very top (handles bounce-back)
		if (currentScrollY <= 0) {
			currentTransform = 0;
			logoRightSide.style.transform = 'translateY(0px)';
			universalLogo.classList.remove('is-scrolling');
			lastScrollY = currentScrollY;
			return;
		}

		// Only move if there's actual scrolling AND animation is enabled
		if (scrollDelta !== 0 && CONFIG.ANIMATION_ENABLED) {
			// Calculate new transform based on scroll direction
			// Scrolling down (positive delta) = move up (negative transform)
			// Scrolling up (negative delta) = move down (positive transform)
			const movement = -scrollDelta * CONFIG.MOVEMENT_RATIO;
			let newTransform = currentTransform + movement;

			// Clamp to limits: -MAX_TRANSFORM (top) to 0 (bottom)
			newTransform = Math.max(-CONFIG.MAX_TRANSFORM, Math.min(0, newTransform));

			// Update current transform and apply
			currentTransform = newTransform;
			logoRightSide.style.transform = `translateY(${currentTransform}px)`;

			// Show scrolling state when not at bottom position
			if (currentTransform < 0) {
				universalLogo.classList.add('is-scrolling');
			} else {
				universalLogo.classList.remove('is-scrolling');
			}
		}

		// Update last scroll position
		lastScrollY = currentScrollY;
	};

		// Update scroll effects
	const updateScrollEffects = () => {
		// Apply direction animation effect
		applyDirectionalAnimation();

		// Handle homepage logo visibility with JS transitions
		if (isHomepage()) {
			const { logoOffsetTop, headerOffset } = getPositions();
			const shouldBeVisible = logoOffsetTop >= headerOffset;

			// Handle visibility changes
			if (shouldBeVisible && !logoVisible) {
				fadeInLogo();
			} else if (!shouldBeVisible && logoVisible) {
				fadeOutLogo();
			}
		}

		ticking = false;
	};

	// Throttled scroll handler using requestAnimationFrame
	const onScroll = () => {
		if (!ticking) {
			requestAnimationFrame(updateScrollEffects);
			ticking = true;
		}
	};

		// Handle resize and load events
	const onResizeOrLoad = () => {
		// Reset logo transforms on load/resize to prevent stuck animations
		resetLogoTransforms();

		if (isHomepage()) {
			const { logoOffsetTop, headerOffset } = getPositions();
			const shouldBeVisible = logoOffsetTop >= headerOffset;

			// Clear any existing timeout
			if (fadeTimeout) clearTimeout(fadeTimeout);

			// Set initial state without animation
			if (shouldBeVisible) {
				homepageLogo.classList.add('is-visible');
				logoVisible = true;
				CONFIG.ANIMATION_ENABLED = true;
			} else {
				homepageLogo.classList.remove('is-visible');
				logoVisible = false;
				CONFIG.ANIMATION_ENABLED = false;
			}
		} else {
			// Enable animation immediately for non-homepage pages
			CONFIG.ANIMATION_ENABLED = true;
		}
	};

	// Initialize logo with direction-based scroll functionality
	const initializeLogo = () => {
		universalLogo.classList.add('has-directional-scroll');
		// Ensure clean initial state
		resetLogoTransforms();

		// Enable animation immediately for non-homepage pages
		if (!isHomepage()) {
			CONFIG.ANIMATION_ENABLED = true;
		}

		// Apply initial animation state based on current scroll position
		applyDirectionalAnimation();
	};

	// Initialize
	initializeLogo();

	// Event listeners
	window.addEventListener('load', onResizeOrLoad);
	window.addEventListener('resize', onResizeOrLoad);
	window.addEventListener('scroll', onScroll, { passive: true });

	// Cleanup function for SPA scenarios
	const cleanup = () => {
		window.removeEventListener('load', onResizeOrLoad);
		window.removeEventListener('resize', onResizeOrLoad);
		window.removeEventListener('scroll', onScroll);

		// Clear any pending timeouts
		if (fadeTimeout) clearTimeout(fadeTimeout);

		// Reset logo state
		universalLogo.classList.remove('has-directional-scroll', 'is-scrolling');
		resetLogoTransforms();
	};

	// Debug info (only in development)
	if (process.env.NODE_ENV === 'development') {
		console.log('Logo reveal initialized:', {
			homepageLogo: !!homepageLogo,
			universalLogo: !!universalLogo,
			hasLogoSVG: !!logoRightSide
		});
	}

	return cleanup;
};

export default handleLogoReveal;
